'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useSession, signOut } from '@/lib/auth-client'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { ThemeToggle } from '@/components/theme-toggle'
import { SearchModal } from '@/components/search-modal'

export function SiteHeader({
  isAdmin = false,
  canBuyingHouse = false,
  canTravelDiary = false,
  canRealEstate = false,
}: {
  isAdmin?: boolean
  canBuyingHouse?: boolean
  canTravelDiary?: boolean
  canRealEstate?: boolean
}) {
  const router = useRouter()
  const { data: session, isPending } = useSession()

  const initials = session?.user.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <header className="bg-background/80 sticky top-0 z-50 border-b backdrop-blur">
      <nav className="container mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
        <Link href="/" className="text-lg font-bold tracking-tight">
          BaseWeb
        </Link>

        {/* Centre nav links */}
        <div className="flex items-center gap-4">
          <Link href="/" className="text-muted-foreground hover:text-foreground text-sm">
            Articles
          </Link>
          {session && (
            <DropdownMenu>
              <DropdownMenuTrigger className="text-muted-foreground hover:text-foreground text-sm focus:outline-none">
                Knowledge
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => router.push('/knowledge/stocks')}>
                  Stocks
                </DropdownMenuItem>
                {canRealEstate && (
                  <DropdownMenuItem onClick={() => router.push('/knowledge/real-estate')}>
                    Real Estate
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {session && (
            <Link href="/feed" className="text-muted-foreground hover:text-foreground text-sm">
              My Feed
            </Link>
          )}
          {session && (
            <DropdownMenu>
              <DropdownMenuTrigger className="text-muted-foreground hover:text-foreground text-sm focus:outline-none">
                Analysis
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => router.push('/finance')}>Stock</DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push('/finance/portfolio')}>
                  Portfolio
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push('/finance/macro')}>
                  US Macroeconomics
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {(canBuyingHouse || canTravelDiary) && (
            <DropdownMenu>
              <DropdownMenuTrigger className="text-muted-foreground hover:text-foreground text-sm focus:outline-none">
                Workflow
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {canBuyingHouse && (
                  <DropdownMenuItem onClick={() => router.push('/buying-house')}>
                    Buying House
                  </DropdownMenuItem>
                )}
                {canTravelDiary && (
                  <DropdownMenuItem onClick={() => router.push('/diary/new')}>
                    Travel Diary
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Right: theme toggle + auth */}
        <div className="flex items-center gap-2">
          <SearchModal />
          <ThemeToggle />

          {isPending ? (
            <Skeleton className="h-8 w-8 rounded-full" />
          ) : session ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="focus-visible:ring-ring rounded-full focus:outline-none focus-visible:ring-2"
                aria-label="User menu"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={session.user.image ?? undefined}
                    alt={session.user.name ?? ''}
                  />
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-muted-foreground text-sm" disabled>
                  {session.user.email}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push('/feed')}>My Feed</DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push('/profile')}>Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push('/profile/style')}>
                  Appearance
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => router.push('/admin')}>Admin</DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => signOut({ fetchOptions: { onSuccess: () => router.refresh() } })}
                >
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link href="/sign-in">
              <Button size="sm">Sign In</Button>
            </Link>
          )}
        </div>
      </nav>
    </header>
  )
}
